// Copyright 2025 Sorama B.V.
import { DialogController } from "aurelia-dialog";
import { autoinject } from "aurelia-framework";
import { CamIv64Device } from "services/Hardware/CamHTTP/CamIV64Helpers";
import { ICamApi } from "services/Hardware/CamHTTP/ICamApi";

export interface FactoryResetModel {
    device?: CamIv64Device; // Required: Device to reset
}

@autoinject
export class FactoryResetDialog {
    private model: FactoryResetModel = {};

    protected isResetting = false;
    protected hasError = false;
    protected errorMessage = "";
    protected isComplete = false;

    constructor(
        private readonly controller: DialogController,
        private readonly camApi: ICamApi
    ) {}

    public activate(factoryResetModel: FactoryResetModel) {
        // Set the model directly
        this.model = factoryResetModel;

        // Make sure we have all required properties
        if (!this.model.device) {
            throw new Error("Device is required for factory reset");
        }
    }

    protected cancel(): void {
        this.controller.cancel();
    }

    protected async performFactoryReset(): Promise<void> {
        try {
            this.isResetting = true;
            this.hasError = false;
            this.errorMessage = "";

            if (!this.model.device) {
                throw new Error("Missing device information.");
            }

            // Perform factory reset
            await this.camApi.performFactoryReset();

            // Set completion state and stop resetting
            this.isResetting = false;
            this.isComplete = true;

            // Give user time to read the success message before closing
            setTimeout(() => {
                this.controller.ok(true);
            }, 3000); // 3 seconds delay
        } catch (e) {
            console.error("Factory reset failed:", e);
            this.isResetting = false;
            this.hasError = true;
            this.errorMessage =
                "Something went wrong when resetting the device to factory settings. Please try again, or contact Sorama if the problem persists.";
            this.isComplete = false;
        }
    }
}
